/* ==========================================================================
   BRASSLUXE MOROCCO — main product module (ES module)
   Powers [data-mpm] (sections/main-product-moroccan.liquid):
     · variant resolution (finish swatches / segmented / pills)
     · gallery (thumbs, arrows, counter, variant media) + lightbox
     · Photos / 3D tabs — lazy window.BrassLuxeFinish.mountScene (guarded)
     · price / compare / unit / SKU / availability / ATC state / ?variant=
     · quantity stepper, sticky mobile ATC (IntersectionObserver)
     · accordions, recently-viewed recorder (localStorage, max 12)
   Mounted via BrassLuxe.mount with full cleanup on section:unload.
   ========================================================================== */

const FINISH_DEFAULTS = {
  'unlacquered brass': { color: '#C9A227', roughness: 0.18, metalness: 1.0 },
  'brushed brass': { color: '#B8912F', roughness: 0.42, metalness: 1.0 },
  'oil-rubbed bronze': { color: '#3B2A1E', roughness: 0.35, metalness: 0.9 },
  'antique copper': { color: '#9E5B33', roughness: 0.3, metalness: 0.95 },
  'polished nickel': { color: '#C8CCD0', roughness: 0.08, metalness: 1.0 },
  'green patina': { color: '#5F8A6E', roughness: 0.65, metalness: 0.4 }
};

const RECENT_KEY = 'blx-recently-viewed';

function norm(value) {
  return String(value || '').trim().toLowerCase();
}

function recordRecentlyViewed(handle) {
  if (!handle) return;
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== handle);
    list.unshift(handle);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
  } catch (e) { /* storage unavailable — ignore */ }
}

function setupProduct(section, BLX) {
  const reduced = BLX && BLX.reducedMotion;
  const jsonEl = section.querySelector('[data-product-json]');
  if (!jsonEl) return undefined;

  let data;
  try {
    data = JSON.parse(jsonEl.textContent);
  } catch (e) {
    return undefined;
  }
  const variants = Array.isArray(data.variants) ? data.variants : [];
  const optionNames = Array.isArray(data.optionNames) ? data.optionNames : [];
  const finishOptionIndex = optionNames.findIndex((n) => norm(n).indexOf('finish') !== -1);

  /* finish map: defaults merged with merchant textarea JSON (normalised keys) */
  const finishMap = Object.assign({}, FINISH_DEFAULTS);
  try {
    const custom = data.finishMapRaw ? JSON.parse(data.finishMapRaw) : {};
    Object.keys(custom).forEach((key) => {
      const f = custom[key] || {};
      finishMap[norm(key)] = {
        color: f.color || '#C9A227',
        roughness: typeof f.roughness === 'number' ? f.roughness : 0.3,
        metalness: typeof f.metalness === 'number' ? f.metalness : 1.0
      };
    });
  } catch (e) { /* invalid merchant JSON — defaults only */ }

  const strings = {
    addToCart: section.dataset.stringAddToCart || 'Add to cart',
    soldOut: section.dataset.stringSoldOut || 'Sold out',
    unavailable: section.dataset.stringUnavailable || 'Unavailable',
    available: section.dataset.stringAvailable || 'Available',
    showingFinish: section.dataset.stringShowingFinish || 'Showing finish'
  };

  const cleanups = [];
  function on(el, ev, fn, opts) {
    if (!el) return;
    el.addEventListener(ev, fn, opts);
    cleanups.push(() => el.removeEventListener(ev, fn, opts));
  }

  /* ---------- state ---------- */
  let currentVariant =
    variants.find((v) => String(v.id) === String(section.dataset.currentVariant)) || variants[0] || null;
  let selectedOptions = currentVariant ? currentVariant.options.slice() : [];

  /* ---------- elements ---------- */
  const form = section.querySelector('[data-ajax-add]');
  const idInput = section.querySelector('[data-variant-id]');
  const priceEl = section.querySelector('[data-price]');
  const compareEl = section.querySelector('[data-compare-price]');
  const unitEl = section.querySelector('[data-unit-price]');
  const skuRow = section.querySelector('[data-sku-row]');
  const skuEl = section.querySelector('[data-sku]');
  const availWrap = section.querySelector('[data-availability]');
  const availText = section.querySelector('[data-availability-text]');
  const addBtn = section.querySelector('[data-add-button]');
  const addLabel = section.querySelector('[data-add-label]');
  const stickyBar = section.querySelector('[data-sticky-bar]');
  const stickyPrice = section.querySelector('[data-sticky-price]');
  const stickyBtn = section.querySelector('[data-sticky-atc]');
  const stickyLabel = section.querySelector('[data-sticky-label]');
  const finishCaption = section.querySelector('[data-finish-caption]');

  /* ---------- finish resolution ---------- */
  function resolveFinish(variant) {
    if (variant && variant.finish_color) {
      const r = parseFloat(variant.finish_roughness);
      return { color: variant.finish_color, roughness: Number.isFinite(r) ? r : 0.3, metalness: 1.0 };
    }
    const name = variant && finishOptionIndex > -1 ? variant.options[finishOptionIndex] : null;
    return finishMap[norm(name)] || finishMap['unlacquered brass'];
  }

  function updateFinishCaption(variant) {
    if (!finishCaption) return;
    if (variant && finishOptionIndex > -1 && variant.options[finishOptionIndex]) {
      finishCaption.textContent = strings.showingFinish + ': ' + variant.options[finishOptionIndex];
    } else {
      finishCaption.textContent = '';
    }
  }

  /* ---------- 3D scene (lazy, guarded) ---------- */
  let finishScene = null;
  let finishMountPromise = null;

  function mountFinishScene() {
    if (finishMountPromise) return finishMountPromise;
    const canvas = section.querySelector('[data-finish-canvas]');
    const fallback = section.querySelector('[data-finish-fallback]');
    if (!canvas) return null;
    const waitForApi = window.BrassLuxeFinish
      ? Promise.resolve(window.BrassLuxeFinish)
      : new Promise((resolve) => {
          window.addEventListener('brassluxe:finish-ready', () => resolve(window.BrassLuxeFinish), { once: true });
        });
    finishMountPromise = waitForApi
      .then((api) => api.mountScene(canvas, resolveFinish(currentVariant)))
      .then((scene) => {
        finishScene = scene || null;
        if (fallback) fallback.hidden = true;
        updateFinishCaption(currentVariant);
        return finishScene;
      })
      .catch((error) => {
        console.error('Brassluxe 3D viewer failed:', error);
        if (fallback) fallback.hidden = false;
        return null;
      });
    return finishMountPromise;
  }

  /* ---------- tabs ---------- */
  const tabs = Array.from(section.querySelectorAll('[data-tab]'));
  function activateTab(name) {
    tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      const panel = section.querySelector('[data-panel="' + tab.dataset.tab + '"]');
      if (panel) panel.hidden = !active;
    });
    if (name === 'finish') mountFinishScene();
  }
  tabs.forEach((tab) => {
    on(tab, 'click', () => activateTab(tab.dataset.tab));
    on(tab, 'keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const next = tabs[(tabs.indexOf(tab) + dir + tabs.length) % tabs.length];
      next.focus();
      activateTab(next.dataset.tab);
    });
  });

  /* ---------- gallery ---------- */
  const thumbs = Array.from(section.querySelectorAll('[data-gallery-thumb]'));
  const mainImgWrap = section.querySelector('[data-gallery-main]');
  const mainImg = mainImgWrap ? mainImgWrap.querySelector('.arch-frame__img') : null;
  const counterEl = section.querySelector('[data-gallery-counter]');
  let galleryIndex = Math.max(0, thumbs.findIndex((t) => t.classList.contains('is-active')));

  function setGallery(index) {
    if (!thumbs.length) return;
    galleryIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
    const thumb = thumbs[galleryIndex];
    thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === galleryIndex);
      if (i === galleryIndex) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    if (mainImg && thumb.dataset.full) {
      mainImg.classList.add('is-swapping');
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = thumb.dataset.full;
      mainImg.alt = thumb.dataset.alt || mainImg.alt;
    }
    if (counterEl) counterEl.textContent = galleryIndex + 1 + ' / ' + thumbs.length;
  }
  if (mainImg) on(mainImg, 'load', () => mainImg.classList.remove('is-swapping'));
  thumbs.forEach((thumb, i) => on(thumb, 'click', () => setGallery(i)));
  on(section.querySelector('[data-gallery-prev]'), 'click', () => setGallery(galleryIndex - 1));
  on(section.querySelector('[data-gallery-next]'), 'click', () => setGallery(galleryIndex + 1));

  /* ---------- lightbox ---------- */
  const lightbox = section.querySelector('[data-lightbox]');
  if (lightbox) {
    const lbImg = lightbox.querySelector('[data-lightbox-img]');
    const lbCounter = lightbox.querySelector('[data-lightbox-counter]');
    const lbClose = lightbox.querySelector('[data-lightbox-close]');
    let lastFocus = null;

    function syncLightbox() {
      const thumb = thumbs[galleryIndex];
      if (lbImg && thumb) {
        lbImg.src = thumb.dataset.full || '';
        lbImg.alt = thumb.dataset.alt || '';
      }
      if (lbCounter) lbCounter.textContent = galleryIndex + 1 + ' / ' + thumbs.length;
    }
    function onKey(e) {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') { setGallery(galleryIndex - 1); syncLightbox(); }
      else if (e.key === 'ArrowRight') { setGallery(galleryIndex + 1); syncLightbox(); }
    }
    function openLightbox() {
      if (!thumbs.length && !mainImg) return;
      lastFocus = document.activeElement;
      syncLightbox();
      lightbox.hidden = false;
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      if (lbClose) lbClose.focus();
    }
    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.hidden = true;
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    on(section.querySelector('[data-gallery-zoom]'), 'click', openLightbox);
    on(lbClose, 'click', closeLightbox);
    on(lightbox.querySelector('[data-lightbox-prev]'), 'click', () => { setGallery(galleryIndex - 1); syncLightbox(); });
    on(lightbox.querySelector('[data-lightbox-next]'), 'click', () => { setGallery(galleryIndex + 1); syncLightbox(); });
    on(lightbox, 'click', (e) => { if (e.target === lightbox) closeLightbox(); });
    cleanups.push(() => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = '';
    });
  }

  /* ---------- options ---------- */
  const optionInputs = Array.from(section.querySelectorAll('[data-option-input]'));

  optionInputs.forEach((input) => {
    /* swatch tint from merged finish map (overrides server default) */
    if (input.hasAttribute('data-finish-swatch')) {
      const f = finishMap[norm(input.dataset.value)];
      if (f) input.style.setProperty('--swatch-color', f.color);
    }
    on(input, 'click', () => selectOption(input));
    on(input, 'keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp') return;
      const group = input.closest('[role="radiogroup"]');
      if (!group) return;
      e.preventDefault();
      const items = Array.from(group.querySelectorAll('[data-option-input]'));
      const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      const next = items[(items.indexOf(input) + dir + items.length) % items.length];
      next.focus();
      selectOption(next);
    });
  });

  function selectOption(input) {
    const pos = parseInt(input.dataset.optionPosition, 10) - 1;
    selectedOptions[pos] = input.dataset.value;
    const group = input.closest('[role="radiogroup"]');
    if (group) {
      group.querySelectorAll('[data-option-input]').forEach((el) => {
        const active = el === input;
        el.classList.toggle('is-selected', active);
        el.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
    const selectedEl = section.querySelector('[data-selected-value="' + (pos + 1) + '"]');
    if (selectedEl) selectedEl.textContent = input.dataset.value;
    onVariantChange();
  }

  function syncOptionUI() {
    optionInputs.forEach((input) => {
      const pos = parseInt(input.dataset.optionPosition, 10) - 1;
      const active = selectedOptions[pos] === input.dataset.value;
      input.classList.toggle('is-selected', active);
      input.setAttribute('aria-checked', active ? 'true' : 'false');
    });
  }

  /* ---------- variant change ---------- */
  function setAddState(enabled, label) {
    if (addBtn) addBtn.disabled = !enabled;
    if (addLabel) addLabel.textContent = label;
    if (stickyBtn) stickyBtn.disabled = !enabled;
    if (stickyLabel) stickyLabel.textContent = label;
  }

  function onVariantChange() {
    const variant =
      variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i])) || null;
    currentVariant = variant;

    if (!variant) {
      setAddState(false, strings.unavailable);
      if (availText) availText.textContent = strings.unavailable;
      if (availWrap) availWrap.classList.add('is-unavailable');
      updateFinishCaption(null);
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price_formatted;
    if (compareEl) {
      compareEl.hidden = !variant.compare_at_formatted;
      compareEl.textContent = variant.compare_at_formatted || '';
    }
    if (unitEl) {
      unitEl.hidden = !variant.unit_price_formatted;
      unitEl.textContent = variant.unit_price_formatted || '';
    }
    if (skuRow && skuEl) {
      skuRow.hidden = !variant.sku;
      skuEl.textContent = variant.sku || '';
    }
    if (availText) availText.textContent = variant.available ? strings.available : strings.soldOut;
    if (availWrap) availWrap.classList.toggle('is-unavailable', !variant.available);
    setAddState(variant.available, variant.available ? strings.addToCart : strings.soldOut);
    if (stickyPrice) stickyPrice.textContent = variant.price_formatted;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* ignore */ }

    if (variant.featured_media_id) {
      const idx = thumbs.findIndex((t) => String(t.dataset.mediaId) === String(variant.featured_media_id));
      if (idx > -1) setGallery(idx);
    }

    if (finishScene && typeof finishScene.setFinish === 'function') {
      finishScene.setFinish(resolveFinish(variant));
    }
    updateFinishCaption(variant);
  }

  /* ---------- quantity ---------- */
  const qtyInput = section.querySelector('[data-quantity-input]');
  function stepQty(delta) {
    if (!qtyInput) return;
    const next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
    qtyInput.value = next;
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  on(section.querySelector('[data-quantity-minus]'), 'click', () => stepQty(-1));
  on(section.querySelector('[data-quantity-plus]'), 'click', () => stepQty(1));
  on(qtyInput, 'change', () => {
    const v = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    qtyInput.value = v;
  });

  /* ---------- sticky mobile ATC ---------- */
  if (stickyBar && form && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', show);
          stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      },
      { threshold: 0 }
    );
    io.observe(form);
    cleanups.push(() => io.disconnect());
    on(stickyBtn, 'click', () => {
      if (addBtn && !addBtn.disabled) addBtn.click();
    });
  }

  /* ---------- accordions (animated, reduced-motion safe) ---------- */
  section.querySelectorAll('[data-accordion]').forEach((details) => {
    const summary = details.querySelector('summary');
    const body = details.querySelector('[data-accordion-body]');
    if (!summary || !body) return;
    on(summary, 'click', (e) => {
      if (reduced || typeof body.animate !== 'function') return; /* native toggle */
      e.preventDefault();
      if (details.open) {
        const h = body.offsetHeight;
        const anim = body.animate(
          [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
          { duration: 260, easing: 'ease' }
        );
        anim.onfinish = () => { details.open = false; };
      } else {
        details.open = true;
        const h = body.offsetHeight;
        body.animate(
          [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }],
          { duration: 320, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      }
    });
  });

  /* ---------- init ---------- */
  syncOptionUI();
  updateFinishCaption(currentVariant);
  recordRecentlyViewed(section.dataset.productHandle);

  /* ---------- cleanup (BrassLuxe.mount on section:unload) ---------- */
  return function cleanup() {
    cleanups.forEach((fn) => fn());
    if (finishScene && typeof finishScene.dispose === 'function') finishScene.dispose();
    finishScene = null;
    finishMountPromise = null;
  };
}

function boot() {
  if (window.BrassLuxe && typeof window.BrassLuxe.mount === 'function') {
    window.BrassLuxe.mount('[data-mpm]', setupProduct);
  } else {
    document.querySelectorAll('[data-mpm]').forEach((el) =>
      setupProduct(el, { reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches })
    );
  }
}

boot();
